import type { WidgetProductId } from "./widget-product-id";

/** Platform-wide locale codes supported by the widget i18n stack. */
export const PLATFORM_LOCALES = ["en-US", "ko-KR", "ja-JP"] as const;

export type PlatformLocale = (typeof PLATFORM_LOCALES)[number];

/** Platform-wide ISO 4217 currency codes used for formatting and config. */
export const PLATFORM_CURRENCIES = ["USD", "KRW", "JPY"] as const;

export type PlatformCurrency = (typeof PLATFORM_CURRENCIES)[number];

export const PLATFORM_LOCALE_LABELS: Record<PlatformLocale, string> = {
  "en-US": "English (US)",
  "ko-KR": "한국어 (Korean)",
  "ja-JP": "日本語 (Japanese)",
};

export const PLATFORM_LOCALE_MENU_LABELS: Record<PlatformLocale, string> = {
  "en-US": "English",
  "ko-KR": "한국어",
  "ja-JP": "日本語",
};

export const PLATFORM_CURRENCY_LABELS: Record<PlatformCurrency, string> = {
  USD: "$ USD (US Dollar)",
  KRW: "₩ KRW (Korean Won)",
  JPY: "¥ JPY (Japanese Yen)",
};

const PRODUCT_SUPPORTED_LOCALES = {
  earn: PLATFORM_LOCALES,
  tokenization: PLATFORM_LOCALES,
} as const satisfies Record<WidgetProductId, readonly PlatformLocale[]>;

const PRODUCT_SUPPORTED_CURRENCIES = {
  earn: PLATFORM_CURRENCIES,
  tokenization: ["USD"],
} as const satisfies Record<WidgetProductId, readonly PlatformCurrency[]>;

export function isPlatformLocale(locale: string): locale is PlatformLocale {
  return (PLATFORM_LOCALES as readonly string[]).includes(locale);
}

export function isPlatformCurrency(currency: string): currency is PlatformCurrency {
  return (PLATFORM_CURRENCIES as readonly string[]).includes(currency);
}

export function getSupportedLocales(productId: WidgetProductId): readonly PlatformLocale[] {
  return PRODUCT_SUPPORTED_LOCALES[productId];
}

export function getSupportedCurrencies(productId: WidgetProductId): readonly PlatformCurrency[] {
  return PRODUCT_SUPPORTED_CURRENCIES[productId];
}

export function isSupportedLocale(
  productId: WidgetProductId,
  locale: string,
): locale is PlatformLocale {
  return (PRODUCT_SUPPORTED_LOCALES[productId] as readonly string[]).includes(locale);
}

export function isSupportedCurrency(
  productId: WidgetProductId,
  currency: string,
): currency is PlatformCurrency {
  return (PRODUCT_SUPPORTED_CURRENCIES[productId] as readonly string[]).includes(currency);
}

export function getLocaleSelectOptions(productId: WidgetProductId): {
  value: PlatformLocale;
  label: string;
}[] {
  return getSupportedLocales(productId).map((locale) => ({
    value: locale,
    label: PLATFORM_LOCALE_LABELS[locale],
  }));
}

export function getCurrencySelectOptions(productId: WidgetProductId): {
  value: PlatformCurrency;
  label: string;
}[] {
  return getSupportedCurrencies(productId).map((currency) => ({
    value: currency,
    label: PLATFORM_CURRENCY_LABELS[currency],
  }));
}

/** Picks the first supported currency for the product when the value is missing or invalid. */
export function coerceCurrencyForProduct(
  productId: "tokenization",
  currency: string | undefined,
): "USD";
export function coerceCurrencyForProduct(
  productId: "earn",
  currency: string | undefined,
): PlatformCurrency;
export function coerceCurrencyForProduct(
  productId: WidgetProductId,
  currency: string | undefined,
): PlatformCurrency;
export function coerceCurrencyForProduct(
  productId: WidgetProductId,
  currency: string | undefined,
): PlatformCurrency {
  if (currency && isSupportedCurrency(productId, currency)) {
    return currency;
  }

  return PRODUCT_SUPPORTED_CURRENCIES[productId][0];
}

/** Picks the first supported locale for the product when the value is missing or invalid. */
export function coerceLocaleForProduct(
  productId: WidgetProductId,
  locale: string | undefined,
): PlatformLocale {
  if (locale && isSupportedLocale(productId, locale)) {
    return locale;
  }

  return PRODUCT_SUPPORTED_LOCALES[productId][0];
}
